import { Link } from "wouter";
import { Youtube } from "lucide-react";

export function Footer() {
  return (
    <footer className="bg-background border-t border-white/10 pt-16 pb-8 relative overflow-hidden">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-32 bg-primary/10 blur-[100px] pointer-events-none rounded-t-[100%]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link href="/" className="flex items-center gap-2 group cursor-pointer inline-flex">
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 shadow-md shadow-primary/20 group-hover:shadow-primary/40 transition-all group-hover:scale-105">
                <img
                  src={`${import.meta.env.BASE_URL}images/fisl-mark-288.webp`}
                  alt="FISL Logo"
                  width={36}
                  height={36}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="font-display font-bold text-lg text-white">FIS Ltd.</span>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              Bridging the gap between emerging AI technology and real-world business transformation for professionals and creators.
            </p>
            <div className="text-sm text-muted-foreground pt-2 space-y-1">
              <p>Wakefield, England, United Kingdom</p>
              <p>aianimatorhub@gmail.com</p>
              <p>+44 7474 110800</p>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Navigation</h4>
            <ul className="space-y-3">
              <li><Link href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">Home</Link></li>
              <li><Link href="/programmes" className="text-sm text-muted-foreground hover:text-primary transition-colors">Programmes</Link></li>
              <li><Link href="/about" className="text-sm text-muted-foreground hover:text-primary transition-colors">About</Link></li>
              <li><Link href="/contact" className="text-sm text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
              <li><Link href="/#reviews" className="text-sm text-muted-foreground hover:text-primary transition-colors">Reviews</Link></li>
            </ul>
          </div>

          {/* Programmes */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Programmes</h4>
            <ul className="space-y-3">
              <li><Link href="/programmes/executive-mentorship" className="text-sm text-muted-foreground hover:text-primary transition-colors">1-to-1 Executive Mentorship</Link></li>
              <li><Link href="/programmes/teen-ai-builders-academy" className="text-sm text-muted-foreground hover:text-primary transition-colors">Teen AI Builders Academy</Link></li>
              <li><Link href="/programmes/group-training" className="text-sm text-muted-foreground hover:text-primary transition-colors">Group Training</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-display font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-3">
              <li><Link href="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link href="/terms-of-service" className="text-sm text-muted-foreground hover:text-primary transition-colors">Terms of Service</Link></li>
              <li><Link href="/cookie-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors">Cookie Policy</Link></li>
              <li>
                <a
                  href="https://checkout.revolut.com/pay/93d9940b-0f35-4ae5-87ae-42c1d1ff95f6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid="link-mentorship-checkout"
                >
                  Pay for Mentorship via Revolut
                </a>
              </li>
              <li>
                <a
                  href="https://aianimator.store"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid="link-ai-animator-store"
                >
                  AI Animator Store
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col gap-0.5 text-center md:text-left">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Future Intelligence Systems Ltd. All rights reserved.
            </p>
          </div>
          <div className="flex gap-4">
            <a href="https://www.facebook.com/profile.php?id=61587408038406" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="Facebook">
              <svg aria-hidden="true" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="https://www.youtube.com/@TheGenerativeCreator01" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="YouTube">
              <Youtube aria-hidden="true" className="w-5 h-5" />
            </a>
            <a href="https://www.tiktok.com/@aianimator_1" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors" aria-label="TikTok">
              <svg aria-hidden="true" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.4V8.62a8.192 8.192 0 0 0 4.77 1.526V6.686h-.001Z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
